<template>
    <b-popover
        id="filterTreatmentPopOver"
        :target="target"
        :show.sync="showPopOver"
        placement="bottomleft"
        @show="onShow"
        @hidden="onHidden"
        class="pl-0 pr-0"
    >
        <!-- eslint-disable-next-line -->
        <template #title><span class="text-title">Filter Treatment</span></template>
        <div class="pb-2">
            <div
                v-show="reducedFilterSelected.length > 0"
                class="pb-2 text-right"
            >
                <span
                    @click.stop="clearFilterSelected"
                    class="text-link text-clear m-2"
                >
                    Clear all
                </span>
            </div>
            <div ref="treeselectComponent" tabindex="0">
                <treeselect
                    v-model="reducedFilterSelected"
                    :multiple="true"
                    :value-consists-of="'LEAF_PRIORITY'"
                    :default-expand-level="1"
                    :options="filterOptionsArray"
                    :disable-branch-nodes="true"
                    :searchable="true"
                    :clearable="false"
                    :clearOnSelect="true"
                    :noChildrenText="'No Category to filter'"
                    :alwaysOpen="true"
                    :disableFuzzyMatching="true"
                    :flattenSearchResults="true"
                    @select="onSelect"
                    @deselect="onSelect"
                >
                    <template slot="value-label" slot-scope="{ node }">
                        {{ getLabelForNode(node) }}
                    </template>
                    <template
                        class="marginNode"
                        slot="option-label"
                        slot-scope="{ node }"
                    >
                        <span v-if="node.isBranch" class="font-weight-bold">
                            {{ node.label }}
                        </span>
                        <span v-else>
                            {{ node.label }}
                        </span>
                    </template>
                </treeselect>
            </div>
        </div>
    </b-popover>
</template>
<script>
import Treeselect from "@riophae/vue-treeselect";
import {
    map,
    startCase,
    filter,
    groupBy,
    mapValues,
    find,
    debounce,
    forEach,
    cloneDeep
} from "lodash";
export default {
    components: {
        Treeselect
    },
    data() {
        return {
            showPopOver: false,
            isToggling: false,
            isFirstUpdate: true,
            filterOptionsArray: [],
            filterOptionsArrayOriginal: [],
            filterSelected: [],
            filterSelectedObject: {},
            isClearFilter: false,
            triggeredFilterTimestamp: 0,
            skipClickOutside: false
        };
    },
    props: {
        target: {
            type: String
        },
        filterOptionsObject: {
            type: Object,
            default() {
                return {};
            }
        },
        useNewTreatmentBoard: {
            type: Boolean,
            default: false
        }
    },
    watch: {
        filterOptionsObject(value, oldValue) {
            if (value != oldValue) {
                this.renderOptionsArray();
            }
        },
        selectedTreatmentBoardFilter() {
            if (
                this.isFirstUpdate &&
                this.selectedTreatmentBoardFilter.length > 0
            ) {
                this.filterSelected = this.selectedTreatmentBoardFilter;
                this.$emit("filter-selected", this.filterSelected);
                this.prepareSelectedObject(this.filterSelected);
                // emit instantly
                this.$emit("filter-data", this.filterSelectedObject);
            }
            this.isFirstUpdate = false;
        }
    },
    mounted() {
        // Add click listener to detect clicks outside the popover
        document.addEventListener("click", this.handleClickOutside);
    },
    beforeDestroy() {
        // Remove the click listener when the component is destroyed
        document.removeEventListener("click", this.handleClickOutside);
    },
    computed: {
        reducedFilterSelected: {
            get() {
                let reducedOne = this.filterSelected.filter(opt => {
                    const [
                        selectedKey,
                        selectedValue,
                        selectedLabel
                    ] = opt.split("|");
                    if (selectedKey == "overnight_stay") {
                        if (this.useNewTreatmentBoard) {
                            return opt;
                        }
                    } else {
                        return opt;
                    }
                });
                this.$emit("filter-selected", reducedOne);
                return reducedOne;
            },
            set(val) {
                this.filterSelected = val;
            }
        },
        selectedTreatmentBoardFilter: {
            get() {
                return (
                    this.$store.state.userPreferences.prefs.treatmentBoardFilter
                        ?.filter || []
                );
            },
            set(value) {
                return this.$store.commit("userPreferences/setSpecificPref", {
                    type: "treatmentBoardFilter",
                    data: { filter: value }
                });
            }
        }
    },
    methods: {
        togglePopOver() {
            if (this.isToggling) {
                return; // Prevents further execution if toggling
            }

            this.isToggling = true;
            this.showPopOver = !this.showPopOver;

            setTimeout(() => {
                this.isToggling = false;
            }, 300);
        },
        onShow() {
            this.renderOptionsArray();
        },
        onHidden() {
            this.isToggling = false;
            this.showPopOver = false;
        },
        renderOptionsArray() {
            this.filterOptionsArrayOriginal = map(
                this.filterOptionsObject,
                (children, key) => {
                    // Handle "overnight_stay" as a standalone node
                    if (key === "overnight_stay" && children) {
                        return {
                            id: key,
                            label: startCase(key),
                            customLabel: startCase(key), // Ensure customLabel consistency
                            isDisabled: !this.useNewTreatmentBoard,
                            children: [
                                {
                                    id: `overnight_stay|no_id|Display Overnight Stay`,
                                    label: "Display Overnight Stay",
                                    customLabel: "Display Overnight Stay",
                                    isDisabled: !this.useNewTreatmentBoard
                                }
                            ]
                        };
                    }

                    // Handle other keys with children
                    return {
                        id: key,
                        label: startCase(key),
                        customLabel: startCase(key),
                        children: map(children, (label, childKey) => ({
                            id: `${key}|${childKey}|${label}`,
                            label: startCase(label), // Ensure label is formatted correctly
                            customLabel: startCase(label)
                        }))
                    };
                }
            );

            this.filterOptionsArray = cloneDeep(
                this.filterOptionsArrayOriginal
            );

            /*
                Check if selected filter exists, if not, push it
                Cater issue when selected that is not in options
            */
            forEach(this.filterSelected, selectedItem => {
                const [
                    selectedKey,
                    selectedValue,
                    selectedLabel
                ] = selectedItem.split("|");

                // Find the parent in the filterOptionsArray
                const parent = find(this.filterOptionsArray, {
                    id: selectedKey
                });

                if (parent) {
                    // Check if the child exists in the parent's children
                    const child = find(parent.children, {
                        id: `${selectedKey}|${selectedValue}|${selectedLabel}`
                    });

                    if (!child) {
                        // If the child is not found, push the missing child
                        parent.children.push({
                            id: `${selectedKey}|${selectedValue}|${selectedLabel}`,
                            label: selectedLabel,
                            customLabel: selectedLabel
                        });
                    }
                }
            });
            this.enableOptions();
        },
        getLabelForNode(node) {
            const selectedItem = find(
                this.filterSelected,
                item => item === node.id
            );
            if (selectedItem) {
                let [key, id, value] = selectedItem.split("|");

                return `${value} (${this.getCustomWords(key)})`;
            }
            return node.label;
        },
        getCustomWords(key) {
            let properWords = startCase(key.split("|"));
            return properWords;
        },
        prepareSelectedObject(params) {
            const splitFilterSelected = map(params, item => item.split("|"));
            const cleanfilterSelected = filter(
                splitFilterSelected,
                item =>
                    item.length >= 2 && item[0] && (item[1] || item[1] === "-1")
            );
            const groupByFilter = groupBy(cleanfilterSelected, item => item[0]);
            this.filterSelectedObject = mapValues(
                groupByFilter,
                (partsArray, key) =>
                    key === "overnight_stay"
                        ? 1
                        : map(partsArray, parts => parts[1])
            );
        },
        async clearFilterSelected(event) {
            // Stop event propagation to prevent accidental popover closure
            event.stopPropagation();
            // Focus the treeselect or any other focusable element before clearing the filters
            await this.focusTreeSelectElement();
            await this.$nextTick(() => {
                this.filterSelected = [];
                this.filterSelectedObject = {};
                this.isClearFilter = true;
                this.$emit("filter-selected", this.filterSelected);
                this.triggerFilter(this.filterSelectedObject, Date.now());
            });
        },
        focusTreeSelectElement() {
            // Access the focusable element directly
            const focusableElement = this.$refs.treeselectComponent;
            if (focusableElement) {
                this.$nextTick(() => {
                    this.$nextTick(() => {
                        focusableElement.focus();
                    });
                });
            }
        },
        // Ensure detect clicks outside the popover to close it
        async handleClickOutside(event) {
            // Wait until the DOM updates and popover is fully rendered
            await this.$nextTick();
            const popoverEl = document.getElementById("filterTreatmentPopOver");
            const targetEl = document.getElementById(this.target);
            const treeSelectEl = this.$refs.treeselectComponent;
            const treeSelectOptionsEl = document.querySelector(
                ".vue-treeselect__list"
            );

            if (this.skipClickOutside) {
                this.skipClickOutside = false;
                return;
            }

            // Check if target, popover, treeselect & treeselect options exist and then apply the contains logic
            if (
                this.showPopOver &&
                popoverEl &&
                targetEl &&
                treeSelectEl &&
                treeSelectOptionsEl &&
                !popoverEl.contains(event.target) &&
                !targetEl.contains(event.target) &&
                !treeSelectEl.contains(event.target) &&
                !treeSelectOptionsEl.contains(event.target)
            ) {
                this.showPopOver = false;
            }
        },
        async onSelect() {
            await this.$nextTick();
            await this.focusTreeSelectElement();
            this.skipClickOutside = true;

            this.prepareSelectedObject(this.filterSelected);
            this.$emit("filter-selected", this.filterSelected);
            if (this.isClearFilter) {
                // no delay for clear filter instant
                this.triggerFilter(this.filterSelectedObject, Date.now());
            } else {
                // add delay 1s for filter selected
                this.update(this.filterSelectedObject, this, Date.now());
            }
        },
        triggerFilter(filterSelectedObject, triggeredFilterTimestamp) {
            let self = this;
            if (triggeredFilterTimestamp < self.triggeredFilterTimestamp) {
                /*to cater clear all filter instantly after select filter options
                this will halt process on filter select delay*/
                return;
            }
            self.triggeredFilterTimestamp = triggeredFilterTimestamp;
            self.disableOptions();
            self.$emit("filter-data", filterSelectedObject);

            if (!self._isTvView) {
                self.saveStoreFilterTreatmentBoardPreference(
                    self.filterSelected
                );
            }
            self.isClearFilter = false;
        },
        update: debounce(
            async (filterSelectedObject, self, triggeredFilterTimestamp) => {
                self.triggerFilter(
                    filterSelectedObject,
                    triggeredFilterTimestamp
                );
            },
            1000
        ),
        disableOptions() {
            this.filterOptionsArray = map(this.filterOptionsArray, option => {
                return {
                    ...option,
                    children: map(option.children, child => ({
                        ...child,
                        isDisabled: true // Disable child nodes dynamically
                    }))
                };
            });
        },
        enableOptions() {
            this.filterOptionsArray = map(this.filterOptionsArray, option => {
                return {
                    ...option,
                    children: map(option.children, child => ({
                        ...child,
                        isDisabled: false // Re-enable child nodes dynamically
                    }))
                };
            });
        },
        async saveStoreFilterTreatmentBoardPreference(value) {
            await this.$store
                .dispatch("userPreferences/updatePref", {
                    category: "treatmentBoardFilter",
                    data: { filter: value }
                })
                .finally(() => {});
        }
    }
};
</script>
<style scoped>
.text-title {
    font-size: 14px;
}

.text-clear {
    font-size: 12px;
}

.marginNode {
    margin-bottom: 2px;
}
</style>
